<script setup lang="ts">
/**
 * Composant ViewerControls - Contrôles de navigation pour le visualiseur 360°
 * Fournit des boutons de navigation directionnelle
 */


defineOptions({
  name: 'ViewerControls'
})

/**
 * Props du composant
 */
interface Props {
  /** Angle actuel (0 à numAngleStep-1) */
  currentAngle: number
  /** Nombre total d'angles affichables */
  numAngleStep: number
  /** Désactiver les contrôles */
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false
})

/**
 * Événements émis par le composant
 */
interface Emits {
  /** Émis pour passer à l'angle précédent */
  (e: 'prev'): void
  /** Émis pour passer à l'angle suivant */
  (e: 'next'): void
}

const emit = defineEmits<Emits>()


/**
 * Gestionnaires d'événements
 */
const handlePrev = (): void => {
  if (!props.disabled) {
    emit('prev')
  }
}

const handleNext = (): void => {
  if (!props.disabled) {
    emit('next')
  }
}


</script>

<template>
  <div class="viewer-controls">
    <!-- Boutons de navigation directionnelle -->
    <div class="navigation-controls">
      <button
        class="viewer-control viewer-control--prev"
        :disabled="disabled"
        @click="handlePrev"
        aria-label="Angle précédent"
        title="Angle précédent"
      >
        <svg
          class="control-icon"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M15 18L9 12L15 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="sr-only">Angle précédent</span>
      </button>

      <button
        class="viewer-control viewer-control--next"
        :disabled="disabled"
        @click="handleNext"
        aria-label="Angle suivant"
        title="Angle suivant"
      >
        <svg
          class="control-icon"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M9 18L15 12L9 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="sr-only">Angle suivant</span>
      </button>
    </div>


  </div>
</template>

<style scoped>
.viewer-controls {
  position: absolute;
  bottom: 1rem;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  pointer-events: none;
  z-index: var(--z-controls, 10);
}

.viewer-controls > * {
  pointer-events: auto;
}

/* Navigation directionnelle */
.navigation-controls {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  padding: 0 var(--spacing-md);
  pointer-events: none;
}

.viewer-control {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  background-color: var(--tab-default);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.3s ease;
  pointer-events: auto;
  box-shadow: none;
}

.viewer-control:hover {
  background-color: var(--tab-active);
  transform: scale(1.1);
}

.viewer-control:focus {
  outline: none;
  border-color: var(--color-primary, #007bff);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
}

.viewer-control:active {
  transform: scale(0.95);
}

.viewer-control:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.control-icon {
  width: 24px;
  height: 24px;
  color: var(--color-text-primary, #333);
}



/* Accessibilité */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* Responsive - Masquer certains éléments sur mobile */
@media (max-width: 768px) {
  .viewer-control {
    width: 40px;
    height: 40px;
  }

  .control-icon {
    width: 20px;
    height: 20px;
  }
}

/* Animation réduite pour accessibilité */
@media (prefers-reduced-motion: reduce) {
  .viewer-control {
    transition: none;
  }
}
</style>